/**
 * ExpandableText — renders text clamped to a few lines with an inline
 * "Show more / Show less" toggle. The toggle only appears when the text
 * actually overflows the clamp, so short values stay clean.
 *
 * Used in the admin tables (demo requests, support, upgrade inquiries) where
 * long free-text fields (notes, messages, use cases) were previously cropped
 * with no way to read the full content.
 */

import { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

// Static literals so Tailwind's scanner picks up the line-clamp utilities.
const CLAMP_CLASS: Record<number, string> = {
  2: 'line-clamp-2',
  3: 'line-clamp-3',
  4: 'line-clamp-4',
  5: 'line-clamp-5',
  6: 'line-clamp-6',
};

interface ExpandableTextProps {
  text: string | null | undefined;
  /** Lines to show when collapsed. Default 3. */
  clampLines?: 2 | 3 | 4 | 5 | 6;
  /** Extra classes for the text paragraph. */
  className?: string;
}

export function ExpandableText({ text, clampLines = 3, className = '' }: ExpandableTextProps) {
  const [expanded, setExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState(false);
  const ref = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (el) {
      // Measured while collapsed (clamp active): overflow => the toggle is needed.
      setIsClamped(el.scrollHeight > el.clientHeight + 1);
    }
  }, [text, clampLines]);

  if (!text) return null;

  return (
    <div className="space-y-1">
      <p
        ref={ref}
        className={`text-xs text-muted-foreground whitespace-pre-line ${expanded ? '' : CLAMP_CLASS[clampLines]} ${className}`}
      >
        {text}
      </p>
      {(isClamped || expanded) && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="inline-flex items-center gap-0.5 text-xs font-medium text-primary hover:underline"
        >
          {expanded ? (
            <>
              Show less <ChevronUp className="h-3 w-3" />
            </>
          ) : (
            <>
              Show more <ChevronDown className="h-3 w-3" />
            </>
          )}
        </button>
      )}
    </div>
  );
}
